<template>
  <section
    ref="feedbackSection"
    class="w-full bg-transparent flex flex-col justify-center items-center h-[100vh] overflow-hidden relative"
  >
    <!-- Carousel Wrapper -->
    <div
      class="relative w-full max-w-6xl flex flex-col items-center justify-center md:mb-80 lg:mb-20"
    >
      <!-- Section Title -->
      <div
        class="flex flex-col items-center text-center w-full max-w-full space-y-3 mb-12"
      >
        <h2
          class="text-lg sm:text-xl lg:text-2xl font-bold tracking-widest text-white"
        >
          TESTIMONIALS
        </h2>
        <h2
          class="text-2xl sm:text-3xl md:text-5xl lg:text-[40px] font-bold text-[#FCF440] text-center leading-tight"
        >
          Discover Our Impact Through The Voices Of Our Users
        </h2>
        <p
          class="text-white text-sm sm:text-base md:text-lg lg:text-[18px] font-light text-center max-w-full sm:max-w-[80%] md:max-w-[70%]"
        >
          Their stories speak for us — real experiences from users who’ve grown
          with our system through innovation, reliability, and service that
          truly matter.
        </p>
      </div>

      <!-- Carousel Container -->
      <div
        class="relative w-full flex items-center justify-center min-h-[350px] sm:min-h-[380px] md:min-h-[400px] lg:min-h-[420px]"
      >
        <!-- Feedback Cards -->
        <div
          v-for="(feedback, index) in feedbacks"
          :key="feedback.id + '-card'"
          class="absolute rounded-3xl flex flex-col items-center justify-start transition-all duration-700 ease-[cubic-bezier(0.22,0.61,0.36,1)] bg-white/20 backdrop-blur-md border border-white/20 w-[260px] sm:w-[320px] md:w-[400px] lg:w-[450px] h-[250px] sm:h-[300px] md:h-[320px] lg:h-[310px] xl:h-[300px] shadow-lg"
          :style="getCardStyle(index)"
          :class="{
            'opacity-100 z-20 scale-100': index === currentIndex,
            'opacity-60 z-10 scale-90': index !== currentIndex,
          }"
        >
          <!-- Profile Circle -->
          <div
            class="absolute -top-[45px] sm:-top-[50px] lg:-top-[55px] flex justify-center w-full transition-all duration-700"
            :class="{
              'opacity-100': index === currentIndex,
              'opacity-60': index !== currentIndex,
            }"
          >
            <img
              :src="feedback.avatar"
              alt="Profile"
              class="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-white shadow-md object-cover transition-all duration-700"
            />
          </div>

          <!-- Card Content -->
          <div
            class="relative flex flex-col items-center text-center px-5 sm:px-6 mt-14 sm:mt-16 md:mt-20"
          >
            <div class="flex flex-col mb-3 sm:mb-4">
              <h3 class="text-base sm:text-lg font-semibold text-white">
                {{ feedback.name }}
              </h3>
              <p class="text-xs sm:text-sm text-green-200">
                {{ feedback.role }}
              </p>
            </div>
            <p
              class="text-white/90 text-xs sm:text-sm md:text-base font-light leading-relaxed line-clamp-3"
            >
              "{{ feedback.feedback }}"
            </p>
            <div>
              <button
                @click="openModal(feedback)"
                class="mt-5 sm:mt-6 px-5 py-2 rounded-full text-sm font-medium bg-white/20 border border-white/20 text-white hover:bg-green1 hover:text-black transition-all duration-300"
              >
                Read More →
              </button>
            </div>
          </div>
        </div>

        <!-- Navigation Buttons -->

        <!-- Small to Medium Devices (buttons at left & right of card) -->
        <div
          class="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-0 sm:px-4 md:px-6 lg:hidden z-0"
        >
          <!-- Left Button -->
          <button
            @click="prevFeedback"
            class="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/40 shadow-md hover:bg-green1 hover:text-black transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 sm:h-6 sm:w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <!-- Right Button -->
          <button
            @click="nextFeedback"
            class="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/40 shadow-md hover:bg-green1 hover:text-black transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 sm:h-6 sm:w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <!-- Large to XL Devices (buttons below the cards) -->
        <div
          class="hidden lg:flex justify-center items-center gap-6 w-full absolute bottom-[-90px] md:bottom-[-100px] xl:bottom-[-20px] z-50"
        >
          <!-- Left Button -->
          <button
            @click="prevFeedback"
            class="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/40 shadow-md hover:bg-green1 hover:text-black transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 sm:h-6 sm:w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <!-- Right Button -->
          <button
            @click="nextFeedback"
            class="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-md border border-white/40 shadow-md hover:bg-green1 hover:text-black transition-all duration-300"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 sm:h-6 sm:w-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </section>

  <!-- Overlay Modal -->
  <transition name="fade">
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black/60 flex justify-center items-center z-50"
    >
      <div
        class="relative bg-white/10 backdrop-blur-md border border-white/40 w-[95%] sm:w-[80%] md:w-[60%] lg:w-[48%] rounded-3xl p-6 flex flex-col items-center text-center shadow-lg overflow-y-auto h-[80vh] lg:h-auto modal-scroll"
      >
        <component
          :is="selectedComponent"
          v-if="selectedComponent"
          @close="closeModal"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import bap from "@/assets/img/bap.jpg";
import yong from "@/assets/img/yong.jpg";
import jinnah from "@/assets/img/jinnah.jpg";
import edjec from "@/assets/img/edjec.jpg";
import ian from "@/assets/img/ian.jpg";
import mark from "@/assets/img/mark.jpg";
// import gsap from "gsap";
import borlioFeedback from "@/components/contents/borlio-feedback.vue";
import yongFeedback from "@/components/contents/yong-feedback.vue";
import jinnahFeedback from "@/components/contents/jinnah-feedback.vue";
import edjecFeedback from "@/components/contents/edjec-feedback.vue";
import ianFeedback from "@/components/contents/ian-feedback.vue";
import markFeedback from "@/components/contents/mark-feedback.vue";

export default {
  name: "FeedbackCarousel",
  components: {
    borlioFeedback,
    yongFeedback,
    jinnahFeedback,
    edjecFeedback,
    ianFeedback,
    markFeedback,
  },
  data() {
    return {
      feedbacks: [
        {
          id: 1,
          name: "JEVANNEL G. BORLIO",
          role: "Assistant Professor I",
          avatar: bap,
          component: borlioFeedback,
          feedback:
            "The implementation of the DNSC-LMS has fundamentally transformed asynchronous learning in Davao del Norte State College.",
        },
        {
          id: 2,
          name: "RHAYMART JOE O. GELLA",
          role: "Supervising Administrative Officer",
          avatar: yong,
          component: yongFeedback,
          feedback:
            "The introduction of the Fund and Requisition Management System (FRMS) revolutionized workflow efficiency.",
        },
        {
          id: 3,
          name: "JINNAH T. ENUMERABLES",
          role: "Administrative Officer V",
          avatar: jinnah,
          component: jinnahFeedback,
          feedback:
            "The implementation of the DNSC-SIS has completely transformed Supply and Property Management operations.",
        },
        {
          id: 4,
          name: "RAQUEL J. EDJEC",
          role: "Administrative Officer V",
          avatar: edjec,
          component: edjecFeedback,
          feedback:
            "The Human Resource Information System greatly enhanced HR efficiency and convenience for all employees.",
        },
        {
          id: 5,
          name: "IAN S. SOMOSOT",
          role: "Associate Professor II",
          avatar: ian,
          component: ianFeedback,
          feedback:
            "Since 2016, I’ve witnessed the shift from tedious manual paperwork to an efficient digital process through FRMS.",
        },
        {
          id: 6,
          name: "MARCK LESTER L. NAVALES",
          role: "Accountant III",
          avatar: mark,
          component: markFeedback,
          feedback:
            "The Unified Digital Ecosystem of DNSC has profoundly transformed my experience of work, service, and education.",
        },
      ],
      currentIndex: 0,
      showModal: false,
      selectedComponent: null,
    };
  },
  watch: {
    showModal(newVal) {
      document.body.style.overflow = newVal ? "hidden" : "";
    },
  },
  methods: {
    nextFeedback() {
      this.currentIndex = (this.currentIndex + 1) % this.feedbacks.length;
    },
    prevFeedback() {
      this.currentIndex =
        (this.currentIndex - 1 + this.feedbacks.length) % this.feedbacks.length;
    },
    getCardStyle(index) {
      const total = this.feedbacks.length;
      const gap = 550;
      let offset = index - this.currentIndex;

      if (offset < -Math.floor(total / 2)) offset += total;
      if (offset > Math.floor(total / 2)) offset -= total;

      if (Math.abs(offset) > 1) return { display: "none" };

      const scale = index === this.currentIndex ? 1.05 : 0.8;
      const opacity = index === this.currentIndex ? 1 : 0.6;

      return {
        transform: `translateX(${offset * gap}px) scale(${scale})`,
        opacity,
        zIndex: index === this.currentIndex ? 20 : 10,
      };
    },
    openModal(feedback) {
      this.selectedComponent = feedback.component;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedComponent = null;
    },
  },
};
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Modal Scrollbar */
.modal-scroll::-webkit-scrollbar {
  width: 8px;
}
.modal-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
}
.modal-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>
